import { registerActions } from "../actionRegistry";
/**
 * Admin Operations
 *
 * Admin-only functions for managing users, artworks, blocked words/tags,
 * advances, licensing, pages, API keys, and other administrative tasks.
 */

import { api } from "../api";
import { getDomElement, getElementValue, setHtml, fadeOut, h } from "../utils/dom";
import { showLoadingOverlay, hideLoadingOverlay, toastSuccess, toastError } from "../ui/messages";
import { closeModalLast, modal, refreshModal } from "../ui/modal";

export function deleteblockedword(id: string): void {
  if (!confirm("Are you sure?")) return;
  api.blockedWords
    .remove({ wordId: id })
    .then(() => {
      toastSuccess("Word removed from blocklist");
      refreshModal();
    })
    .catch((err: unknown) => {
      toastError((err as Error).message);
    });
}

export function addblockedword(): void {
  api.blockedWords
    .add({ word: getElementValue("blockword") })
    .then(() => {
      toastSuccess("Word added to blocklist");
      refreshModal();
    })
    .catch((err: unknown) => {
      toastError((err as Error).message);
    });
}

export async function hideallartworks(id: string): Promise<void> {
  showLoadingOverlay("Toggling artwork visibility...");
  try {
    await api.artists.hideAll({ id: Number(id) });
    refreshModal();
    setTimeout(hideLoadingOverlay, 3000);
  } catch (err: unknown) {
    toastError((err as Error).message);
  }
}

export async function hidedup(id: string): Promise<void> {
  const master = getElementValue(`master-${id}`);
  if (parseInt(master) <= 0) return;
  try {
    await api.artwork.hideDup({ checksum: id, masterId: Number(master) });
    fadeOut(`dup-${id}`);
    toastSuccess(`Artworks hidden except #${master}`);
  } catch (err: unknown) {
    toastError((err as Error).message);
  }
}

export async function deleteadvance(id: string): Promise<void> {
  if (!confirm("Are you sure you want to delete this advance payout?")) return;
  try {
    await api.advance.delete({ id: Number(id) });
    toastSuccess("Advance deleted");
    setTimeout(refreshModal, 100);
  } catch (err: unknown) {
    toastError((err as Error).message);
  }
}

export async function addadvance(user_id: string): Promise<void> {
  const currency = getElementValue("advance_currency");
  const amount = getElementValue("advance_amount");

  if (currency !== "EUR" && currency !== "USD") {
    toastError("Currency must be USD or EUR");
    return;
  }
  // Number (not parseInt): "" and "12abc" must fail the guard, not slip through as NaN.
  const amountNum = Number(amount);
  if (!Number.isFinite(amountNum) || amountNum <= 0) {
    toastError("Amount must be numeric and greater than 0");
    return;
  }

  try {
    await api.advance.add({ userId: Number(user_id), amount: amountNum, currency });
    closeModalLast();
    toastSuccess("Advance added");
    setTimeout(refreshModal, 500);
  } catch (err: unknown) {
    toastError((err as Error).message);
  }
}

export async function setlicensing(user_id: string, licensing_time: string): Promise<void> {
  const status = getDomElement("licensing_status");

  /** Repaint the status line. `text` is appended, so it can never be markup. */
  const say = (color: string, text: string, spinner = false): void => {
    if (!status) return;
    const line = h("span", "", text);
    line.style.color = color;
    if (spinner) {
      const img = document.createElement("img");
      img.src = "https://static.pictufy.com/assets/img/loader.gif";
      status.replaceChildren(img, line);
    } else {
      status.replaceChildren(line);
    }
  };

  say("", `Please wait, setting licensing time to ${licensing_time} months`, true);
  try {
    await api.artists.saveLicenseType({
      user_id: Number(user_id),
      licensingtime: Number(licensing_time),
    });
    say("darkgreen", "Licensing time updated");
  } catch (err: unknown) {
    // Was interpolated into innerHTML unescaped, while `ui/messages.ts:53`
    // escapes the identical value on the way to a toast — with a comment saying
    // why: server messages embed user input verbatim (`${filename}: only jpg …`).
    say("darkred", (err as Error).message);
  }
}

export async function switchblockmode(artist_id: string, block_mode: string): Promise<void> {
  showLoadingOverlay("Switching block mode...");
  try {
    await api.artists.switchBlockMode({
      artistId: Number(artist_id),
      blockMode: block_mode as "normal" | "reverse",
    });
    refreshModal();
  } catch (err: unknown) {
    alert((err as Error).message);
  } finally {
    hideLoadingOverlay();
  }
}

export async function userlogin(user_id: string): Promise<void> {
  try {
    await api.users.becomeUser({ id: Number(user_id) });
    location.href = "/";
  } catch (err: unknown) {
    toastError((err as Error).message);
  }
}

export async function accountreturn(): Promise<void> {
  try {
    await api.users.returnUser();
    location.href = "/";
  } catch (err: unknown) {
    toastError((err as Error).message);
  }
}

export async function dofeature(): Promise<void> {
  try {
    await api.artists.feature({
      id: Number(getElementValue("feature_id")),
      collection: getElementValue("feature_collection_id"),
    });
    closeModalLast();
    toastSuccess("Artist featured");
  } catch (err: unknown) {
    toastError((err as Error).message);
  }
}

export async function admmarkpaid(id: string, period: string): Promise<void> {
  if (!confirm("Are you sure?")) return;
  showLoadingOverlay("Marking transactions as paid out...");
  try {
    await api.payout.markPaid({ userId: Number(id), period });
    setHtml(
      `msg-${id}`,
      '<i style="color: #50AF2F;" class="fas fa-circle"></i> <span style="font-size: 0.8em; position: relative; bottom: 1px; padding: 0px 0px 0px 5px;">Paid</span>'
    );
  } catch (err: unknown) {
    toastError((err as Error).message);
  } finally {
    hideLoadingOverlay();
  }
}

/*
 * `checkrights()` was here — the client half of the "check licensing rights"
 * tool, where a reseller pasted Artwork IDs and got back which had expired. It
 * read `#checkrights_input`, called `api.artwork.checkRights`, and built a ~10
 * line HTML string with a list/images toggle.
 *
 * It had been unreachable since 2026-03-31, when `apps/legacy/apps/checkrights.php`
 * — the only page that ever rendered `#checkrights_input` — was deleted by a
 * commit titled "removing unused files in app". With no emitter, `getElementValue`
 * returned "" and the function early-returned on its first line.
 *
 * Retired by decision 2026-08-18: the client function, the `artwork.checkRights`
 * tRPC procedure and the `api` binding all go. Recover the original page from the
 * `legacy-php-final` tag if it is ever wanted back — note it gated on
 * `$auth["partner_id"]` (any reseller) while the raft procedure had drifted to
 * `adminProcedure`, so the audience would need deciding.
 */

export async function aiartist(id: string): Promise<void> {
  showLoadingOverlay("Adding artworks to queue...");
  try {
    await api.metadata.queueAiArtist({ id: Number(id) });
    location.href = "/ai-metadata/queue";
  } catch (err: unknown) {
    hideLoadingOverlay();
    toastError((err as Error).message);
  }
}

export async function sendwelcome(id: string): Promise<void> {
  showLoadingOverlay("Sending e-mail, please wait...");
  try {
    await api.newsletter.sendWelcomeEmail({ id });
    hideLoadingOverlay();
    setHtml(
      "welcometarget",
      ' &nbsp; <span style="opacity: .3;" class="button medium grey"><i class="fas fa-envelope"></i> Welcome e-mail sent</span>'
    );
    toastSuccess("Welcome e-mail has been sent");
  } catch (err: unknown) {
    hideLoadingOverlay();
    toastError((err as Error).message);
  }
}

type PrestatusAction =
  | "app-unl"
  | "app-50"
  | "app-100"
  | "app-150"
  | "app-200"
  | "app-500"
  | "reject"
  | "snooze";

export async function prestatus(id: string, action: PrestatusAction): Promise<void> {
  if (!confirm("Are you sure?")) return;
  showLoadingOverlay("Please wait...");
  try {
    await api.prestatus.update({ id: Number(id), action });
    fadeOut(`pre_${id}`);
  } catch (err: unknown) {
    toastError((err as Error).message);
  } finally {
    hideLoadingOverlay();
  }
}

export async function deleteartwork(id: string): Promise<void> {
  if (!confirm("Are you sure you want to delete this artwork?")) return;
  try {
    await api.artwork.delete({ id: Number(id) });
    toastSuccess("Artwork deleted");
    window.dispatchEvent(new CustomEvent("artwork-deleted", { detail: { id: Number(id) } }));
    if (!getDomElement("dashboard-has-artworks")) {
      closeModalLast();
    }
  } catch (err: unknown) {
    alert((err as Error).message);
  }
}

export async function savepage(
  page_id: string,
  vals: { title: string; url: string; security: string; isBlank: boolean }
): Promise<void> {
  try {
    await api.pages.save({
      page_id,
      page_title: vals.title,
      page_url: vals.url,
      security: vals.security as
        | "PUBLIC"
        | "USER"
        | "MODERATOR"
        | "CURATOR"
        | "ADMIN"
        | "NOT_LOGGED_IN",
      is_blank: vals.isBlank,
    });
    closeModalLast();
    toastSuccess("Page saved");
    refreshModal();
  } catch (err: unknown) {
    toastError((err as Error).message);
  }
}

export async function newpage(): Promise<void> {
  try {
    const response = await api.pages.create();
    modal(`editpage:${response.page_id}`, "dynamic");
  } catch (err: unknown) {
    toastError((err as Error).message);
  }
}

export async function deletepage(page_id: string): Promise<void> {
  if (!confirm("Are you sure?")) return;
  try {
    await api.pages.delete({ pageId: page_id });
    refreshModal();
    toastSuccess("Page deleted");
  } catch (err: unknown) {
    toastError((err as Error).message);
  }
}

export async function apikey(id: string): Promise<void> {
  if (!confirm("Are you sure you want to assign an API key to this reseller?")) return;
  try {
    await api.apiKeys.generate({ id: Number(id) });
    refreshModal();
    toastSuccess("API key assigned to reseller");
  } catch (err: unknown) {
    toastError((err as Error).message);
  }
}

registerActions({
  hidedup,
  accountreturn,
  admmarkpaid,
  aiartist,
  prestatus,
});
